import React from 'react';
import PropTypes from 'prop-types';
import { LuAlarmClock, LuArrowRight, LuEye, LuGitMerge, LuLock, LuShieldCheck } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import { useTranslation } from '../../../i18n/LanguageContext';
import { isMirrorVisit } from '../../../utils/hybridVisitMirror';
import { visitCompletionWindow } from './visitSteps';
import { Notice } from './VisitUi';

const DATE_TIME = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' };

/**
 * Banners shown on every step (as the old header did): why the visit is
 * read-only, the hybrid mirror note, completion dates still missing on a
 * completed visit, and the completion countdown once the visit window ended.
 */
const VisitNotices = ({ visit, isReadOnly, isRestrictedUser, isPastVisit, isAdmin, pendingCompletionLabels, onGoToCompletion }) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`visitModal.notices.${key}`, vars);

  const notices = [];

  if (isRestrictedUser) {
    notices.push(<Notice key="restricted" tone="info" icon={LuEye}>{label('restricted')}</Notice>);
  } else if (isReadOnly) {
    notices.push(<Notice key="readOnly" tone="danger" icon={LuLock}>{label('readOnly')}</Notice>);
  } else if (isPastVisit && isAdmin) {
    notices.push(<Notice key="admin" tone="warning" icon={LuShieldCheck}>{label('adminEditable')}</Notice>);
  }

  if (isMirrorVisit(visit)) {
    notices.push(
      <Notice key="mirror" tone="accent" icon={LuGitMerge}>
        {label('mirror', { name: visit.mirrorSourceEmployeeName || label('mirrorDefaultName') })}
        {visit.mirrorSourceDeleted ? ` ${label('mirrorSourceDeleted')}` : ''}
      </Notice>
    );
  }

  // Completed while the D — Tamamlanma Tarihleri dates are still being collected
  if (pendingCompletionLabels.length > 0) {
    notices.push(
      <Notice
        key="pending"
        tone="warning"
        title={label('pendingTitle', { items: pendingCompletionLabels.join(', ') })}
        action={(
          <Button size="sm" variant="warning" onClick={onGoToCompletion}>
            {label('pendingAction')}
            <LuArrowRight aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
          </Button>
        )}
      >
        {label('pendingText')}
      </Notice>
    );
  }

  // Visit window over but still inside the 72-hour completion grace period
  const completion = !isPastVisit ? visitCompletionWindow(visit) : null;
  const today = new Date();
  if (completion && today > completion.lastDate) {
    const hoursRemaining = Math.ceil((completion.completionDeadline - today) / (1000 * 60 * 60));
    notices.push(
      <Notice
        key="deadline"
        tone="warning"
        icon={LuAlarmClock}
        title={`${label('deadlineTitle')} · ${label('deadlineHours', { count: hoursRemaining })}`}
      >
        {label('deadlineText', {
          end: completion.lastDate.toLocaleDateString(locale, DATE_TIME),
          deadline: completion.completionDeadline.toLocaleDateString(locale, DATE_TIME),
        })}
      </Notice>
    );
  }

  if (notices.length === 0) return null;
  return <div className="tw-space-y-2">{notices}</div>;
};

VisitNotices.propTypes = {
  visit: PropTypes.object,
  isReadOnly: PropTypes.bool,
  isRestrictedUser: PropTypes.bool,
  isPastVisit: PropTypes.bool,
  isAdmin: PropTypes.bool,
  pendingCompletionLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
  onGoToCompletion: PropTypes.func.isRequired,
};

export default VisitNotices;
